<template>
  <div class="conversions">
    <div class="conversions__filters">
      <app-form-group label="Period (UTC)" class="form-group--no-indent">
        <VueCtkDateTimePicker
          id="conversion_period" v-model="dateRange" :custom-shortcuts="dateShortcuts"
          button-color="#77b017" color="#77b017" class="custom-date-time-picker custom-date-time-picker--range"
          format="YYYY-MM-DD" formatted="ll" label="Select dates" locale="en"
          no-header no-label noClearButton overlay range @is-hidden="applyFilters"
        />
      </app-form-group>
      <app-form-group label="Date basis" class="form-group--no-indent">
        <app-select v-model="dateBasis" :options="dateOptions" :reduce="item => item.value" label="label" :clearable="false" @input="applyFilters" />
      </app-form-group>
      <app-form-group label="Group by" class="form-group--no-indent">
        <app-select v-model="granularity" :options="granularityOptions" :reduce="item => item.value" label="label" :clearable="false" @input="applyFilters" />
      </app-form-group>
      <app-form-group v-for="field in fields" :key="field.key" :label="field.label" class="form-group--no-indent">
        <app-select v-model="filters[field.key]" :options="options[field.key] || []" :reduce="item => item.value" label="label" multiple :closeOnSelect="false" @input="filterChanged(field.key)" />
      </app-form-group>
      <app-form-group label="Lead ID" class="form-group--no-indent">
        <app-input v-model="leadId" type="number" min="1" @input="scheduleFilters" />
      </app-form-group>
    </div>
    <p v-if="optionsError" role="alert">{{ optionsError }} <button type="button" @click="loadOptions">Retry filter options</button></p>
    <app-spinner v-if="loading" />
    <p v-else-if="error" role="alert">{{ error }}</p>
    <template v-else-if="summary">
      <p v-if="summary.unrecorded_success_count" class="conversions__notice" role="status">History is incomplete: {{ summary.unrecorded_success_count }} accepted conversions across all providers have no successful journal entry. Historical evidence backfill is required.</p>
      <p v-if="summary.missing_date_count" class="conversions__notice">{{ summary.missing_date_count }} operations matching the non-date filters have no selected date and are excluded from this period.</p>
      <div class="conversions__heading">
        <h2 class="title">Summary</h2>
        <button class="conversions__info-btn" type="button" aria-label="About conversion analytics" aria-haspopup="dialog" @click="$modal.show('conversion-analytics-info')">i</button>
      </div>
      <p v-if="!summary.counts.total" class="conversions__empty" role="status">No recorded operations match these filters.</p>
      <template v-else>
        <v-client-table
          :data="[summary.counts]" :columns="metrics.map(metric => metric.key)"
          :options="summaryTableOptions" class="table-default"
        />
        <div class="conversions__charts">
          <section>
            <h2 class="title">Accepted uploads and successful retractions</h2>
            <highcharts :options="timelineChart" />
          </section>
          <section>
            <h2 class="title">Current operation results</h2>
            <highcharts :options="statusChart" />
          </section>
        </div>
        <h2 class="title">Provider / conversion action</h2>
        <app-cells>
          <app-filters v-model="breakdownGroup" :filters="breakdownGroups" name="conversion-breakdown-group" />
        </app-cells>
        <section v-for="group in groupedBreakdown" :key="`${breakdownGroup}:${group.key}`" class="conversions__group">
          <h3 class="title">{{ label(breakdownGroup === 'provider' ? 'provider' : 'action', group.key) }}</h3>
          <v-client-table
            :data="group.rows" :columns="[breakdownGroup === 'provider' ? 'action_code' : 'provider', 'total', 'uploads', 'retractions']"
            :options="breakdownTableOptions" class="table-default"
          >
            <template #provider="{ row }">{{ label('provider', row.provider) }}</template>
            <template #action_code="{ row }">{{ label('action', row.action_code) }}</template>
          </v-client-table>
        </section>
      </template>
    </template>
    <modal name="conversion-analytics-info" :adaptive="true" height="auto" :scrollable="true" :width="640" class="modal-not-overflow">
      <div class="popup" role="dialog" aria-modal="true" aria-labelledby="conversion-info-title">
        <button class="popup__close" type="button" aria-label="Close explanation" @click="$modal.hide('conversion-analytics-info')"></button>
        <div id="conversion-info-title" class="popup__title">How to read conversion analytics</div>
        <div class="popup__body conversions__explanation">
          <h3>What is counted?</h3>
          <p>Recorded operations, not unique leads. Retries count once. Accepted uploads mean the provider accepted the conversion, not that it attributed it to an ad. Exclusions are decisions not to send; pending, unknown and error results are shown separately.</p>
          <h3>Retractions</h3>
          <p>A retraction withdraws a previously accepted conversion, for example after a lead is marked as spam or a double request. Only successful withdrawals count as Successful retractions. The original upload stays in history; support depends on the provider.</p>
          <h3>Dates</h3>
          <p>All dates use UTC. Latest operation activity uses completion, then latest attempt, then recording date. Original CRM event uses the source event; for retractions, the original upload's event. Lead creation uses the lead's creation date.</p>
          <h3>Flow and status</h3>
          <p>Processing channel describes how the operation was recorded. Current lead flow, status and V2 stage describe the lead now, not when the conversion was sent.</p>
          <p>A history warning means some accepted conversions are missing from the journal.</p>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { Chart } from 'highcharts-vue'
import { getLeadsConversionOptions, getLeadsConversionSummary } from '@/http'

const fields = [
  { key: 'provider', label: 'Provider' }, { key: 'action', label: 'Conversion action' },
  { key: 'kind', label: 'Operation type' }, { key: 'status', label: 'Operation result' },
  { key: 'flow', label: 'Processing channel' }, { key: 'lead_flow', label: 'Current lead flow' },
  { key: 'lead_status', label: 'Current legacy status' }, { key: 'stage', label: 'Current V2 stage' },
  { key: 'reason', label: 'Exclusion / retraction reason' }
]
const emptyFilters = () => Object.fromEntries(fields.map(field => [field.key, []]))
const defaultPeriod = days => [moment.utc().subtract(days - 1, 'days').format('YYYY-MM-DD'), moment.utc().format('YYYY-MM-DD')]
const choices = entries => entries.map(([value, label]) => ({ value, label }))

export default {
  name: 'CRMAnalyticsConversions',
  components: { highcharts: Chart, VueCtkDateTimePicker },
  data() {
    return {
      fields,
      filterTimer: null,
      dateShortcuts: [
        ...[7, 30, 60, 90, 180].map(days => ({ key: `last${days}Days`, label: `Last ${days} days`, value: days })),
        { key: 'thisYear', label: 'This year', value: 'year' },
        { key: 'lastYear', label: 'Last year', value: '-year' }
      ],
      filters: emptyFilters(),
      options: {},
      optionsError: '',
      period: defaultPeriod(30),
      dateBasis: 'operation',
      granularity: 'day',
      leadId: '',
      summary: null,
      breakdownGroup: 'provider',
      breakdownGroups: choices([['provider', 'Provider'], ['action_code', 'Conversion']]),
      loading: false,
      error: '',
      requestId: 0,
      dateOptions: choices([['operation', 'Latest operation activity'], ['event', 'Original CRM event'], ['lead', 'Lead creation']]),
      granularityOptions: choices([['day', 'Day'], ['week', 'Week'], ['month', 'Month']]),
      metrics: [{ key: 'uploads', label: 'Accepted uploads' }, { key: 'retractions', label: 'Successful retractions' }, { key: 'exclusions', label: 'Exclusions' }, { key: 'pending', label: 'Pending' }, { key: 'unknown', label: 'Unknown outcome' }, { key: 'errors', label: 'Errors / unavailable' }],
    }
  },
  computed: {
    dateRange: {
      get() { return { start: this.period[0], end: this.period[1] } },
      set(value) { this.period = value ? [value.start, value.end] : [] }
    },
    summaryTableOptions() {
      return this.tableOptions(Object.fromEntries(this.metrics.map(metric => [metric.key, metric.label])), 1)
    },
    groupedBreakdown() {
      const groups = new Map()
      const rows = this.summary ? this.summary.breakdown : []
      rows.forEach(row => {
        const key = row[this.breakdownGroup]
        if (!groups.has(key)) groups.set(key, { key, rows: [] })
        groups.get(key).rows.push(row)
      })
      return [...groups.values()]
    },
    breakdownTableOptions() {
      return this.tableOptions({ provider: 'Provider', action_code: 'Conversion action', total: 'Operations', uploads: 'Accepted uploads', retractions: 'Successful retractions' }, this.summary.breakdown.length)
    },
    timelineChart() {
      return { chart: { type: 'column' },
        title: { text: null },
        credits: { enabled: false },
        xAxis: { categories: this.summary.series.map(row => row.date), title: { text: 'Period starting (UTC)' } },
        yAxis: { min: 0, allowDecimals: false, title: { text: 'Operations' } },
        series: [{ name: 'Uploads', data: this.summary.series.map(row => row.uploads) }, { name: 'Retractions', data: this.summary.series.map(row => row.retractions) }] }
    },
    statusChart() {
      return { chart: { type: 'bar' },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: { categories: this.summary.statuses.map(row => this.label('status', row.status)) },
        yAxis: { min: 0, allowDecimals: false, title: { text: 'Operations' } },
        series: [{ name: 'Operations', data: this.summary.statuses.map(row => row.count) }] }
    }
  },
  watch: {
    '$route.query': 'restoreAndLoad'
  },
  created() {
    this.loadOptions()
    this.restoreAndLoad()
  },
  beforeDestroy() {
    clearTimeout(this.filterTimer)
    this.requestId++
  },
  methods: {
    tableOptions(headings, count) {
      return {
        headings,
        pagination: { show: false },
        sortable: [],
        perPage: Math.max(1, count),
        texts: { noResults: 'No matching entries' },
        rowClassCallback: () => 'table-default__row',
        resizableColumns: false
      }
    },
    label(key, value) {
      const choice = (this.options[key] || []).find(item => item.value === value)
      return choice ? choice.label : (value || '—')
    },
    filterChanged(key) {
      if (key === 'stage' && this.filters.stage.length) this.filters.lead_flow = ['v2']
      if (key === 'lead_flow' && (this.filters.lead_flow.length !== 1 || this.filters.lead_flow[0] !== 'v2')) this.filters.stage = []
      this.applyFilters()
    },
    async loadOptions() {
      this.optionsError = ''
      try { this.options = (await getLeadsConversionOptions()).data } catch (error) { this.optionsError = 'Could not load filter options.' }
    },
    restoreAndLoad() {
      clearTimeout(this.filterTimer)
      const query = this.$route.query
      this.period = [query.date_from || defaultPeriod(30)[0], query.date_to || defaultPeriod(30)[1]]
      this.dateBasis = query.date_basis || 'operation'
      this.granularity = query.granularity || 'day'
      this.leadId = query.lead_id || ''
      this.filters = emptyFilters()
      fields.forEach(field => { this.filters[field.key] = query[field.key] ? String(query[field.key]).split(',') : [] })
      this.load()
    },
    params() {
      const result = { date_from: this.period[0], date_to: this.period[1], date_basis: this.dateBasis, granularity: this.granularity }
      fields.forEach(field => { if (this.filters[field.key].length) result[field.key] = this.filters[field.key].join(',') })
      if (this.leadId) result.lead_id = String(this.leadId)
      return result
    },
    scheduleFilters() {
      clearTimeout(this.filterTimer)
      this.requestId++
      this.filterTimer = setTimeout(() => this.applyFilters(), 350)
    },
    applyFilters() {
      clearTimeout(this.filterTimer)
      if (!this.period || !this.period[0] || !this.period[1]) { this.error = 'Select both period dates.'; return }
      const query = this.params()
      const current = this.$route.query
      if (Object.keys(query).length === Object.keys(current).length && Object.keys(query).every(key => String(query[key]) === String(current[key]))) this.load()
      else this.$router.push({ query })
    },
    async load() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const params = this.params()
        const summary = await getLeadsConversionSummary(params)
        if (requestId !== this.requestId) return
        this.summary = summary.data
      } catch (error) {
        if (requestId === this.requestId) this.error = (error.response && error.response.data && error.response.data.details) || 'Could not load conversions. Please refresh.'
      } finally { if (requestId === this.requestId) this.loading = false }
    },

  }
}
</script>

<style scoped lang="scss">
.conversions {
  &__filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin: 20px 0; > * { min-width: 0; } }
  .custom-date-time-picker.date-time-picker { width: 100%; min-width: 0; max-width: 100%; }
  &__heading { display: flex; align-items: center; gap: 12px; margin: 20px 0 12px; .title { margin: 0; } }
  &__info-btn { width: 22px; height: 22px; border-radius: 50%; border: 1px solid #77b017; color: #77b017; background: #fff; font-size: 12px; font-weight: 700; line-height: 1; cursor: pointer; }
  &__empty { margin: 24px 0; color: $color-text-base; font-size: 14px; }
  &__explanation { color: $color-text-base; font-size: 14px; line-height: 1.6; h3 { margin: 20px 0 8px; font-size: 16px; &:first-child { margin-top: 0; } } p { margin: 0 0 12px; } }
  &__notice { background: #fff5dc; padding: 12px; margin: 12px 0; }
  &__group { margin: 24px 0; }
  &__charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin: 24px 0; }
  @media (max-width: 600px) { &__filters { grid-template-columns: minmax(0, 1fr); } }
  @media (max-width: 900px) { &__charts { grid-template-columns: minmax(0, 1fr); } }
}
</style>
